// docs/116 CI5: sitting at the dice table. The view zooms in to the table top as if you had pulled up a
// stool: worn felt in a wooden rim, the candle, the coins of the pot, the other players round the far
// side from the chest up and the watchers behind them, the dice tumbling in from whoever threw. Watch
// mode is the same table without my seat. Sounds are not played here (`DiceDirector` owns them).
import { classLook, heroToken } from '../../../../game/heroKit';
import { heroLookNow } from '../../../../core/heroes/looks';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Badge, Button, LiveRegion, Segmented, useReducedMotion } from '../../../components';
import { smoothServerNow } from '../../../../core/time';
import { CLASS_FRAMES } from '../../../../core/sim/adapt';
import { bustChance, handName, popcount, TEMPER_WORD } from './dice';
import { dieAt, restSpot, throwEnd } from './diceMotion';
import {
  ART_H, ART_W, CANDLE, DIE, FAR_Y, KITTY, NEAR, POT, SEAT_COL, coinSpot, diceZone, farX, paintBackdrop, paintCandle, paintChair, paintChips, paintCoin, paintCreature,
  paintDie, paintGlass, paintGlow, paintHeap, paintShadow, paintTable, type Atlas, type Frame,
} from './dicePaint';
import { reactionOf, resultOf, type DiceResult, type Reaction } from './reactions';
import { DICE_STAKES, type DiceActions, type DiceSeatView, type DiceView } from './types';
import { playInn } from './diceSounds';
import './dice.css';

export { RoomDice, type RoomDiceProps } from './RoomDice';
export { DiceDirector } from './DiceDirector';
export { reactionOf, type Reaction } from './reactions';

export interface DicePayer { id: bigint; key: string; name: string; gold: number }
export interface DiceSpectator { name: string; frame: number }
export interface DiceTableProps {
  view: DiceView;
  actions: DiceActions;
  /** My heroes who can pay (the stool's hero pays the stake). */
  payers: DicePayer[];
  /** My identity hex. */
  me: string;
  /** 'seat': I came to sit (or sat); 'watch': the same table without my seat's buttons. */
  mode: 'seat' | 'watch';
  /** The others in the room watching, drawn behind the far side. */
  spectators: DiceSpectator[];
  onClose(): void;
  /** Server ms (default `smoothServerNow`); the harness passes its engine's clock. */
  now?: () => number;
}

// ---- the creature atlas ----------------------------------------------------------------------------
let atlasP: Promise<Atlas | undefined> | undefined;
function loadAtlas(): Promise<Atlas | undefined> {
  atlasP ??= (async () => {
    try {
      const r = await fetch('/assets/oryx/creatures.json');
      if (!r.ok) return undefined;
      const j = (await r.json()) as { frames: Record<string, { frame: Frame }> };
      const img = new Image();
      img.src = '/assets/oryx/creatures.png';
      await img.decode();
      return { img, frames: Object.fromEntries(Object.entries(j.frames).map(([k, v]) => [k, v.frame])) };
    } catch { return undefined; }
  })();
  return atlasP;
}

/** A seat's figure: a hero in their kit (a token, docs/145), a regular by their folk frame. */
export const seatFrame = (s: Pick<DiceSeatView, 'frame' | 'classId' | 'kind' | 'heroId'>): number =>
  s.kind === 'hero' ? heroToken((s.heroId !== undefined ? heroLookNow(s.heroId) : undefined) ?? classLook(s.classId)) : s.frame > 0 ? s.frame : CLASS_FRAMES[s.classId ?? 'fighter'] ?? 1;

// ---- where everyone sits ------------------------------------------------------------------------------
export interface Place { seat: number; x: number; near: boolean }
/** My stool is the near edge; the others go round the far side left to right in seat order. */
export function placesOf(view: DiceView, seated: boolean): Place[] {
  const mine = seated ? view.seats.findIndex((s) => s.mine) : -1;
  const far = view.seats.map((_, i) => i).filter((i) => i !== mine);
  const out = far.map((seat, k) => ({ seat, x: farX(k, far.length), near: false }));
  if (mine >= 0) out.push({ seat: mine, x: NEAR.x, near: true });
  return out.sort((a, b) => a.seat - b.seat);
}

const SPECTATOR_X = [46, 274, 132, 188, 18, 302];
const chipSpot = (p: Place) => (p.near ? { x: 112, y: 160 } : { x: p.x + 17, y: FAR_Y + 13 });
const winSpot = (p: Place) => (p.near ? { x: 200, y: 158 } : { x: p.x - 16, y: FAR_Y + 13 });
const handOrigin = (p: Place | undefined, throwN: number, i: number) => (p?.near ? { x: 150 + i * 10, y: 196 } : { x: (p?.x ?? 160) - 8 + i * 8 + (throwN % 5), y: FAR_Y + 2 });

interface Bubble { id: number; x: number; y: number; text: string; tone: 'cheer' | 'groan' | 'gasp' | 'plain'; small?: boolean }
interface CoinRun { t0: number; n: number; to: Array<{ x: number; y: number; n: number }>; kitty: boolean }
interface Hop { t0: number; seat: number }

function useScale(): number {
  const get = () => (typeof window === 'undefined' ? 4 : Math.max(2, Math.floor(Math.min(window.innerWidth / ART_W, window.innerHeight / ART_H))));
  const [s, setS] = useState(get);
  useEffect(() => { const on = () => setS(get()); window.addEventListener('resize', on); return () => window.removeEventListener('resize', on); }, []);
  return s;
}

function stateLine(v: DiceView, s: DiceSeatView, i: number): string {
  if (s.kind === 'empty') return s.state === 'out' ? 'got up' : 'an empty stool';
  if (v.state === 'open') return s.kind === 'regular' ? 'waiting for a game' : 'ready';
  if (s.won) return `won ${s.won} gold`;
  switch (s.state) {
    case 'playing': return v.dice.some((d) => d) ? `${v.pushes} push${v.pushes === 1 ? '' : 'es'} left` : 'to throw';
    case 'stood': { const n = handName(s.hand); return /^\d/.test(n) ? `stood on ${n}` : `${n} · ${s.score}`; }
    case 'bust': return s.hand.every((d) => d === 1) ? 'the Gallows' : 'bust';
    case 'waiting': return v.turn >= 0 && i < v.turn ? '' : 'to come';
    default: return '';
  }
}

/** The zoomed-in dice table (an overlay over the village view). */
export function DiceTable({ view, actions, payers, mode, spectators, onClose, now = smoothServerNow }: DiceTableProps) {
  const S = useScale();
  const reduced = useReducedMotion();
  const canvas = useRef<HTMLCanvasElement>(null);
  const [atlas, setAtlas] = useState<Atlas>();
  useEffect(() => { void loadAtlas().then((a) => a && setAtlas(a)); }, []);

  const mySeat = view.seats.findIndex((s) => s.mine);
  const seated = mySeat >= 0 && mode === 'seat';
  const places = useMemo(() => placesOf(view, seated), [view, seated]);
  const myTurn = seated && view.state === 'playing' && view.turn === mySeat && view.seats[mySeat]?.state === 'playing';
  const thrownYet = view.dice.some((d) => d > 0);

  // ---- what moves, kept in refs for the painter -----------------------------------------------------
  const live = useRef({ view, places, reduced });
  live.current = { view, places, reduced };
  const throwAnim = useRef<{ throwN: number; t0: number; mask: number; seat: number } | undefined>(undefined);
  // which throw last moved each die (its rest spot); kept in step with the view as it renders
  const moved = useRef({ throwN: view.throwN, m: [view.throwN, view.throwN, view.throwN] as [number, number, number] });
  if (view.throwN !== moved.current.throwN) {
    for (let i = 0; i < 3; i++) if (view.thrown & (1 << i)) moved.current.m[i] = view.throwN;
    moved.current.throwN = view.throwN;
  }
  const coinRun = useRef<CoinRun | undefined>(undefined);
  const potShown = useRef({ from: view.pot + view.kitty, to: view.pot, t0: 0 });
  const hops = useRef<Hop[]>([]);
  const wait = useRef({ deadline: view.deadlineMs, total: Math.max(1, view.deadlineMs - now()) });
  const [marked, setMarked] = useState(0);
  const [bubbles, setBubbles] = useState<Bubble[]>([]);
  const [result, setResult] = useState<DiceResult & { show: boolean }>();
  const [busy, setBusy] = useState(false);
  const [stake, setStake] = useState<number>(view.stake || 1);
  const [nowMs, setNowMs] = useState(now);
  const prev = useRef<DiceView | undefined>(undefined);
  const timers = useRef<number[]>([]);
  const bubbleN = useRef(0);
  useEffect(() => () => { for (const t of timers.current) clearTimeout(t); }, []);
  useEffect(() => { const id = setInterval(() => setNowMs(now()), 200); return () => clearInterval(id); }, [now]);
  useEffect(() => { if (view.stake) setStake(view.stake); }, [view.stake]);

  const later = useCallback((ms: number, fn: () => void) => { timers.current.push(window.setTimeout(fn, ms)); }, []);
  const placeOf = (seat: number) => live.current.places.find((p) => p.seat === seat);

  const say = useCallback((r: Reaction, v: DiceView) => {
    const spots: Array<Omit<Bubble, 'id'>> = [];
    const tone: Bubble['tone'] = r.kind === 'cheer' || r.kind === 'win' ? 'cheer' : r.kind === 'gasp' ? 'gasp' : r.kind === 'phew' ? 'plain' : 'groan';
    const at = (seat: number) => { const p = placeOf(seat); return p ? (p.near ? { x: 64, y: 150 } : { x: p.x, y: FAR_Y - 40 }) : undefined; };
    if (r.seat >= 0 && r.line) { const a = at(r.seat); if (a) spots.push({ ...a, text: r.line, tone }); }
    const others = v.seats.map((s, i) => ({ s, i })).filter(({ s, i }) => s.kind !== 'empty' && i !== r.seat && !s.mine);
    const k0 = r.seed % 7;
    others.slice(0, r.big ? 2 : 1).forEach(({ i }, k) => { const a = at(i); if (a) spots.push({ ...a, text: r.chorus[(k0 + k) % r.chorus.length] ?? '', tone, small: true }); });
    spectators.slice(0, r.big ? 3 : 1).forEach((_, j) => spots.push({ x: SPECTATOR_X[j] ?? 160, y: 17, text: r.chorus[(k0 + j + 1) % r.chorus.length] ?? '', tone, small: true }));
    const made = spots.map((b) => ({ ...b, id: ++bubbleN.current }));
    setBubbles((bs) => [...bs, ...made]);
    later(2300, () => setBubbles((bs) => bs.filter((b) => !made.includes(b))));
    const t0 = performance.now();
    hops.current = [...hops.current.filter((h) => t0 - h.t0 < 900), ...(r.seat >= 0 ? [{ t0, seat: r.seat }] : []), ...(r.big || r.kind === 'win' ? others.map(({ i }) => ({ t0: t0 + 120, seat: i })) : []), ...(r.kind === 'cheer' || r.kind === 'win' ? [{ t0: t0 + 60, seat: -10 }] : [])];
  }, [spectators, later]);

  useEffect(() => {
    const p = prev.current;
    prev.current = view;
    if (!p) return;
    const t = performance.now();
    if (view.throwN !== p.throwN) {
      throwAnim.current = { throwN: view.throwN, t0: t, mask: view.thrown, seat: view.turn };
      setMarked(0);
    }
    if (view.turn !== p.turn || view.gameN !== p.gameN) setMarked(0);
    if (view.deadlineMs !== wait.current.deadline) wait.current = { deadline: view.deadlineMs, total: Math.max(1000, view.deadlineMs - now()) };
    if (view.gameN !== p.gameN) potShown.current = { from: view.kitty === 0 && p.kitty ? p.kitty : 0, to: view.pot, t0: t };
    if (view.state === 'settling' && p.state === 'playing') {
      const won = view.winners.map((i) => ({ i, n: view.seats[i]?.won ?? 0 }));
      const to = won.map(({ i, n }) => { const pl = placeOf(i); const s = pl ? winSpot(pl) : POT; return { x: s.x, y: s.y, n: Math.min(n, 30) }; });
      coinRun.current = view.last === 'kitty' ? { t0: t + 250, n: Math.min(p.pot, 40), to: [{ ...KITTY, n: Math.min(p.pot, 40) }], kitty: true } : { t0: t + 250, n: Math.min(p.pot, 40), to, kitty: false };
    }
    if (view.state === 'open' && p.state !== 'open') coinRun.current = undefined;
    const r = reactionOf(p, view);
    const res = resultOf(p, view);
    const d = (ms: number) => (live.current.reduced ? 0 : ms);
    if (r?.ooh) later(d(60), () => {
      const b: Bubble = { id: ++bubbleN.current, x: SPECTATOR_X[0]!, y: 17, text: 'Ooh…', tone: 'gasp', small: true };
      setBubbles((bs) => [...bs, b]);
      later(1200, () => setBubbles((bs) => bs.filter((x) => x !== b)));
    });
    if (r) later(d(r.delayMs), () => say(r, view));
    if (res) {
      later(d(res.delayMs), () => setResult({ ...res, show: true }));
      later(d(res.delayMs) + (res.tone === 'plain' ? 1600 : 2400), () => setResult((x) => (x && x.key === res.key ? { ...x, show: false } : x)));
    }
  }, [view, now, later, say]);

  // ---- the painter ----------------------------------------------------------------------------------
  const baked = useMemo(() => {
    if (typeof document === 'undefined') return undefined;
    const wall = document.createElement('canvas'); wall.width = ART_W; wall.height = ART_H;
    paintBackdrop(wall.getContext('2d')!);
    const table = document.createElement('canvas'); table.width = ART_W; table.height = ART_H;
    paintTable(table.getContext('2d')!);
    return { wall, table };
  }, []);
  const markedRef = useRef(marked);
  markedRef.current = marked;
  const specRef = useRef(spectators);
  specRef.current = spectators;

  useEffect(() => {
    const cv = canvas.current;
    if (!cv || !baked) return;
    const c = cv.getContext('2d')!;
    c.imageSmoothingEnabled = false;
    let raf = 0;
    const draw = () => {
      raf = requestAnimationFrame(draw);
      const { view: v, places: pls, reduced: still } = live.current;
      const t = performance.now();
      const ms = still ? 0 : t;
      const hopOf = (seat: number) => {
        if (still) return 0;
        let y = 0;
        for (const h of hops.current) if (h.seat === seat && t >= h.t0 && t - h.t0 < 640) { const u = (t - h.t0) / 320; y = Math.max(y, Math.round(4 * Math.abs(Math.sin(u * Math.PI)))); }
        return y;
      };
      c.clearRect(0, 0, ART_W, ART_H);
      c.drawImage(baked.wall, 0, 0);
      // the watchers behind
      if (atlas) specRef.current.slice(0, SPECTATOR_X.length).forEach((sp, j) => paintCreature(c, atlas, sp.frame || 39, SPECTATOR_X[j]! - 24, 16 - hopOf(-10) * (j % 2 ? 1 : 0), 2, 0.78, j % 2 === 1));
      // the far side: chairs and players from the chest up
      for (const p of pls) {
        if (p.near) continue;
        const s = v.seats[p.seat]!;
        const turn = v.state === 'playing' && v.turn === p.seat;
        if (turn) {
          c.save(); c.globalCompositeOperation = 'lighter';
          const g = c.createRadialGradient(p.x, FAR_Y - 18, 2, p.x, FAR_Y - 18, 30);
          g.addColorStop(0, 'rgba(255,200,110,0.35)'); g.addColorStop(1, 'rgba(0,0,0,0)');
          c.fillStyle = g; c.fillRect(p.x - 32, FAR_Y - 50, 64, 52); c.restore();
        }
        paintChair(c, p.x, FAR_Y - 34, s.kind === 'empty');
        if (s.kind !== 'empty' && atlas) paintCreature(c, atlas, seatFrame(s), p.x - 24, FAR_Y - 37 - hopOf(p.seat), 2, s.state === 'bust' || s.state === 'out' ? 0.3 : 0);
      }
      c.drawImage(baked.table, 0, 0);
      // wait: the candle burns down through a hero's choice; the hourglass shows every wait
      const deadline = v.deadlineMs, left = deadline > 0 ? Math.max(0, deadline - now()) / wait.current.total : 1;
      const heroTurn = v.state === 'playing' && v.turn >= 0 && v.seats[v.turn]?.kind === 'hero' && v.seats[v.turn]?.state === 'playing';
      paintGlass(c, Math.min(1, left), v.state !== 'open' && deadline > 0, heroTurn && deadline - now() < 8000, t);
      paintCandle(c, ms, 1);
      // chips by every stool in the game
      if (v.state !== 'open') for (const p of pls) { const s = v.seats[p.seat]!; if ((s.kind !== 'empty' || s.state === 'out') && v.stake) { const cs = chipSpot(p); paintChips(c, cs.x, cs.y, Math.min(6, v.stake + 1), SEAT_COL[p.seat]!); } }
      // the pot (growing as the stakes go in), the kitty
      const run = coinRun.current;
      const pa = potShown.current;
      let pot = v.pot;
      if (!still && pa.t0 && t - pa.t0 < 700) pot = Math.round(pa.from + (pa.to - pa.from) * ((t - pa.t0) / 700));
      if (run) {
        const sent = still ? run.n : Math.max(0, Math.min(run.n, Math.floor((t - run.t0) / 40)));
        pot = run.n - sent;
      }
      paintHeap(c, POT.x, POT.y, Math.min(pot, 60));
      if (v.kitty > 0 && !(run?.kitty && !still && t - run.t0 < run.n * 40 + 520)) paintHeap(c, KITTY.x, KITTY.y, Math.min(v.kitty, 30));
      if (run) {
        // the coins slide to the winner(s), one after another
        const total = run.to.reduce((a, b) => a + b.n, 0) || 1;
        let k = 0;
        const landed = run.to.map(() => 0);
        for (let w = 0; w < run.to.length; w++) {
          const dest = run.to[w]!;
          const n = Math.max(1, Math.round((dest.n / total) * run.n));
          for (let j = 0; j < n; j++, k++) {
            const s0 = run.t0 + k * 40, u = still ? 1 : Math.max(0, Math.min(1, (t - s0) / 480));
            if (t < s0 && !still) continue;
            if (u >= 1) { landed[w]!++; continue; }
            const [ox, oy] = coinSpot(k);
            const e = 1 - (1 - u) * (1 - u);
            const x = POT.x + ox + (dest.x - POT.x - ox) * e, y = POT.y + oy + (dest.y - POT.y - oy) * e - 10 * Math.sin(u * Math.PI);
            paintCoin(c, x - 3, y, k);
          }
        }
        run.to.forEach((d, w) => { if (landed[w] && !(run.kitty)) paintHeap(c, d.x, d.y, landed[w]); });
      }
      // the dice: shadows first, then the dice back to front
      const anim = throwAnim.current;
      const drawn: Array<{ x: number; y: number; z: number; pose: 'flat' | 'tilt' | 'edge'; face: number; i: number; moving: boolean }> = [];
      for (let i = 0; i < 3; i++) {
        const f = v.dice[i];
        if (!f) continue;
        const rs = restSpot(moved.current.m[i]!, i);
        const rest = diceZone(rs.u, rs.v);
        if (anim && !still && anim.throwN === v.throwN && anim.mask & (1 << i) && t - anim.t0 < throwEnd(anim.throwN, anim.mask) + 50) {
          const a = dieAt(anim.throwN, i, t - anim.t0, f);
          const o = handOrigin(pls.find((p) => p.seat === anim.seat), anim.throwN, i);
          drawn.push({ x: o.x + (rest.x - o.x) * a.p, y: o.y + (rest.y - o.y) * a.p, z: a.z, pose: a.pose, face: a.face, i, moving: a.moving });
        } else drawn.push({ x: rest.x, y: rest.y, z: 0, pose: 'flat', face: f, i, moving: false });
      }
      for (const d of drawn) paintShadow(c, d.x, d.y + DIE / 2 - 1, d.z);
      const mk = markedRef.current;
      const dim = v.state !== 'playing';
      for (const d of drawn.sort((a, b) => a.y - b.y)) {
        const lifted = mk & (1 << d.i) ? 2 : 0;
        paintDie(c, d.x - DIE / 2, d.y - DIE / 2 - d.z - lifted, d.face, d.pose, { marked: !!(mk & (1 << d.i)) && !d.moving, dim });
      }
      paintGlow(c, ms, 1);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, [baked, atlas, now]);

  // ---- acting ------------------------------------------------------------------------------------------
  const act = useCallback(async (fn: () => Promise<boolean>) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  }, []);
  const toggle = (i: number) => { if (!myTurn || !thrownYet || view.pushes <= 0) return; playInn('inn.coin_flip', i + view.throwN, { gain: 0.25 }); setMarked((m) => m ^ (1 << i)); };
  const free = view.seats.some((s) => s.kind === 'empty') || view.seats.some((s) => s.kind === 'regular');
  const canSit = view.state === 'open' && free && payers.length > 0;
  const k = popcount(marked);
  const skullsLeft = view.dice.filter((d, i) => d === 1 && !(marked & (1 << i))).length;
  const secs = Math.max(0, Math.ceil((view.deadlineMs - nowMs) / 1000));
  const turnSeat = view.turn >= 0 ? view.seats[view.turn] : undefined;
  const pay = payers.find((p) => p.id === view.seats[mySeat]?.heroId);
  const shortOf = pay ? pay.gold < stake : false;

  const style = { '--s': S, width: ART_W * S, height: ART_H * S } as CSSProperties;
  const hint = myTurn && thrownYet
    ? (k ? `Push ${k}: ${Math.round(bustChance(k) * 100)}% to bust${skullsLeft ? ` · ${skullsLeft} skull${skullsLeft > 1 ? 's' : ''} kept, worth nothing` : ''}` : 'Click the dice you would throw again — or stand.')
    : '';

  return (
    <div className={`fk-dice ${reduced ? 'fk-dice--still' : ''}`} role="dialog" aria-label="The dice table: Hearth Bones" data-testid="dice-panel" data-state={view.state} data-mode={mode} data-seated={seated ? 'true' : 'false'} data-throw={view.throwN} data-game={view.gameN}>
      <div className="fk-dice__stage" style={style}>
        <canvas ref={canvas} className="fk-dice__canvas" width={ART_W} height={ART_H} aria-hidden="true" />
        <header className="fk-dice__head">
          <div className="fk-dice__title"><span className="fk-dice__name">Hearth Bones</span>
            {view.stake > 0 && <Badge tone="gold">{view.stake} gold a head</Badge>}
            {mode === 'watch' && <Badge tone="grey">watching</Badge>}
          </div>
          <Button small variant="ghost" onClick={onClose} data-testid="dice-close">{seated ? 'Back to the room' : 'Leave the table'}</Button>
        </header>
        {/* the pot, the kitty, the wait */}
        <div className="fk-dice__tag fk-dice__tag--pot" style={{ left: POT.x * S, top: (POT.y + 5) * S }}>{view.pot > 0 ? `${view.pot} gold` : view.state === 'settling' ? '' : 'the pot'}</div>
        {view.kitty > 0 && <div className="fk-dice__tag fk-dice__tag--kitty" style={{ left: KITTY.x * S, top: (KITTY.y + 5) * S }}>kitty {view.kitty}</div>}
        {view.state !== 'open' && view.deadlineMs > 0 && <div className={`fk-dice__tag fk-dice__tag--wait ${secs <= 8 && turnSeat?.kind === 'hero' && view.state === 'playing' ? 'fk-dice__tag--urgent' : ''}`} style={{ left: 58 * S, top: 118 * S }}>{view.state === 'settling' ? `next game ${secs}s` : turnSeat?.kind === 'hero' && turnSeat.state === 'playing' ? `${secs}s` : ''}</div>}
        <span className="fk-dice__candle-hit" style={{ left: (CANDLE.x - 4) * S, top: (CANDLE.y - 22) * S }} aria-hidden="true" />
        {/* name plates */}
        {places.map((p) => {
          const s = view.seats[p.seat]!;
          const turn = view.state === 'playing' && view.turn === p.seat;
          const cls = ['fk-dice__plate', p.near ? 'fk-dice__plate--near' : '', turn ? 'fk-dice__plate--turn' : '', s.kind === 'empty' ? 'fk-dice__plate--empty' : '', s.state === 'bust' ? 'fk-dice__plate--bust' : '', view.winners.includes(p.seat) && view.state === 'settling' ? 'fk-dice__plate--won' : ''].filter(Boolean).join(' ');
          const line = stateLine(view, s, p.seat);
          return (
            <div key={p.seat} className={cls} style={{ left: (p.near ? 46 : p.x) * S, top: (p.near ? 167 : FAR_Y - 4) * S, borderColor: SEAT_COL[p.seat] }}>
              <span className="fk-dice__plate-name">{s.kind === 'empty' ? '—' : s.name}{s.mine && <em> (you)</em>}</span>
              {s.kind === 'regular' && s.temper !== undefined && <span className="fk-dice__plate-temper">{TEMPER_WORD[s.temper] ?? ''} regular</span>}
              {line && <span className="fk-dice__plate-line">{line}</span>}
            </div>
          );
        })}
        {/* the dice I can mark */}
        {myTurn && thrownYet && view.pushes > 0 && [0, 1, 2].map((i) => {
          const rs = restSpot(moved.current.m[i]!, i), at = diceZone(rs.u, rs.v);
          return (
            <button key={i} type="button" className={`fk-dice__die ${marked & (1 << i) ? 'fk-dice__die--marked' : ''}`}
              style={{ left: (at.x - 8) * S, top: (at.y - 9) * S, width: 16 * S, height: 16 * S }}
              aria-pressed={!!(marked & (1 << i))} aria-label={`Die ${i + 1}: ${view.dice[i] === 1 ? 'a skull' : view.dice[i]}${marked & (1 << i) ? ', to throw again' : ''}`}
              onClick={() => toggle(i)} data-testid="dice-die" data-die={i} />
          );
        })}
        {/* words over heads */}
        {bubbles.map((b) => (
          <div key={b.id} className={`fk-dice__bubble fk-dice__bubble--${b.tone} ${b.small ? 'fk-dice__bubble--small' : ''}`} style={{ left: b.x * S, top: b.y * S }}>{b.text}</div>
        ))}
        {result?.show && (
          <div key={result.key} className={`fk-dice__result fk-dice__result--${result.tone}`} style={{ top: 92 * S }} aria-hidden="true">{result.text}</div>
        )}
        {view.state === 'open' && (
          <div className="fk-dice__rules" style={{ top: 112 * S }}>
            <b>Hearth Bones.</b> Throw three dice; skulls (1s) count nothing. Then push (throw again any dice) up to twice, or stand.
            A skull on a pushed die is a <b>bust</b>. 4-5-6 is the <b>Hearth</b> (19); three of a kind beats all; three 6s are the <b>Crowns</b>. Highest hand takes the pot.
          </div>
        )}
        <LiveRegion text={result?.show ? result.text : ''} />
        {/* my seat's buttons */}
        <div className="fk-dice__tray">
          {hint && <div className="fk-dice__hint">{hint}</div>}
          <div className="fk-dice__buttons">
            {!seated && canSit && (mode === 'seat' || payers.length > 0) && payers.map((p) => (
              <Button key={p.key} primary={mode === 'seat'} small={mode === 'watch'} disabled={busy} onClick={() => void act(() => actions.sit(p.id))} data-testid="dice-sit">Sit {p.name} down <span className="fk-dice__gold">{p.gold}g</span></Button>
            ))}
            {!seated && mode === 'seat' && !canSit && view.state === 'open' && <span className="fk-dice__wait">{payers.length ? 'Every stool is taken.' : 'None of your heroes has coin to play.'}</span>}
            {!seated && view.state !== 'open' && <span className="fk-dice__wait">{view.state === 'settling' ? 'Settling up…' : turnSeat && turnSeat.state === 'playing' ? `${turnSeat.name} ${thrownYet ? 'is deciding…' : 'to throw…'}` : 'Next to throw…'}</span>}
            {seated && view.state === 'open' && <>
              <Segmented label="Stake" value={stake} onChange={(v) => { setStake(v); playInn('inn.chips', v, { gain: 0.4 }); }} options={DICE_STAKES.map((s) => ({ value: s, label: `${s} gold` }))} />
              <Button primary disabled={busy || shortOf} onClick={() => void act(() => actions.start(stake))} data-testid="dice-start">{shortOf ? `${pay?.name} is short` : 'Start the game'}</Button>
              <Button small variant="ghost" disabled={busy} onClick={() => void act(() => actions.stand())} data-testid="dice-stand">Get up</Button>
            </>}
            {seated && myTurn && !thrownYet && <Button primary size="lg" disabled={busy} onClick={() => void act(() => actions.roll())} data-testid="dice-roll">Throw the bones</Button>}
            {seated && myTurn && thrownYet && <>
              <Button primary disabled={busy || !k || view.pushes <= 0} onClick={() => void act(() => actions.push(marked))} data-testid="dice-push">Push {k ? `(${k})` : ''}</Button>
              <Button disabled={busy} onClick={() => void act(() => actions.hold())} data-testid="dice-hold">Stand on {handName(view.dice)}</Button>
              <span className="fk-dice__pushes" aria-label={`${view.pushes} pushes left`}>{Array.from({ length: 2 }, (_, i) => <i key={i} className={i < view.pushes ? 'on' : ''} />)}</span>
            </>}
            {seated && view.state === 'playing' && !myTurn && <>
              <span className="fk-dice__wait">{turnSeat && turnSeat.state === 'playing' ? `${turnSeat.name} ${view.dice.some((d) => d) ? 'is deciding…' : 'to throw…'}` : view.seats[mySeat]?.state === 'waiting' ? 'Your turn is coming.' : 'Waiting on the others…'}</span>
              <Button small variant="ghost" disabled={busy} title="Your stake stays in the pot" onClick={() => void act(() => actions.stand())} data-testid="dice-forfeit">Get up (forfeit)</Button>
            </>}
            {seated && view.state === 'settling' && <span className="fk-dice__wait">{view.seats[mySeat]?.won ? `You take ${view.seats[mySeat].won} gold!` : 'Next game soon…'}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
